import { useEffect, useMemo, useState } from "react";

import useApiHelper from "../../hooks/useApiHelper";
import useNotification from "../../components/shared/Notifications/useNotifications";
import { ITicketAttachment } from "../../models/Tickets/Ticket";
import { isImageAttachment } from "../../utils/ticketAttachments";

/**
 * Attachment blobs are fetched with the Firebase token, so they can't be used
 * as plain <img src> URLs - each image is pulled down once and kept as an
 * object URL for the lifetime of the view.
 */
export default function useTicketAttachments(
  ticketId: string | undefined,
  attachments: ITicketAttachment[] | undefined,
) {
  const { getFile } = useApiHelper();
  const { addNotification } = useNotification();
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});

  // Keyed on ids rather than the array so a refetch returning equal data
  // doesn't re-download every image. Each image is fetched from the ticket it
  // belongs to, which a merge preview spreads across several tickets.
  const imageKeys = useMemo(
    () =>
      (attachments ?? [])
        .filter(
          (a) => isImageAttachment(a.contentType) && (a.ticketId || ticketId),
        )
        .map((a) => `${a.ticketId || ticketId}/${a.id}`)
        .join(","),
    [attachments, ticketId],
  );

  useEffect(() => {
    if (!imageKeys) {
      setPreviewUrls({});
      return;
    }

    const objectUrls: string[] = [];
    let isDisposed = false;

    (async () => {
      for (const key of imageKeys.split(",")) {
        const [attachmentTicketId, attachmentId] = key.split("/");
        try {
          const blob = await getFile(
            `/ticket/${attachmentTicketId}/attachments/${attachmentId}/view`,
          );
          if (isDisposed) return;

          const url = URL.createObjectURL(blob);
          objectUrls.push(url);
          setPreviewUrls((prev) => ({ ...prev, [attachmentId]: url }));
        } catch {
          // Keep the view usable even if a preview fails.
        }
      }
    })();

    return () => {
      isDisposed = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
      setPreviewUrls({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageKeys]);

  const downloadAttachment = async (attachment: ITicketAttachment) => {
    const attachmentTicketId = attachment.ticketId || ticketId;
    if (!attachmentTicketId) return;

    try {
      const blob = await getFile(
        `/ticket/${attachmentTicketId}/attachments/${attachment.id}/download`,
      );
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = attachment.fileName || `attachment-${attachment.id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch {
      addNotification({
        variant: "error",
        primaryText: "Failed to download attachment",
      });
    }
  };

  return { previewUrls, downloadAttachment };
}
